import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { AppCrashPanel } from './app-crash-panel.js';

/**
 * Top-level error boundary for the routed content. Catches render-time
 * exceptions inside the app shell (routes, sections, the `@noma-eco/*`
 * embedded components) and renders `<AppCrashPanel>` instead of a blank
 * white page.
 *
 * Position in the provider chain:
 *   <QueryClientProvider>
 *     <AuthKitProvider>
 *       <AccessTokenBridge>
 *         <AppErrorBoundary>      ← this
 *           {children}            ← <RouterProvider> + routed surface
 *         </AppErrorBoundary>
 *       </AccessTokenBridge>
 *     </AuthKitProvider>
 *   </QueryClientProvider>
 *
 * Inside, not outside, the QueryClientProvider — so the boundary can call
 * `queryClient.clear()` on reset.
 *
 * Built on `Sentry.ErrorBoundary`: Sentry captures the crash itself and hands
 * the fallback its own `eventId`, which `AppCrashPanel` shows in a `DebugBlock`
 * so a quoted reference opens the crash issue (with stack) directly. `onReset`
 * keeps the `queryClient.clear()` + redirect recovery; the `onError` console
 * line carries the `eventId` (forwarded by `consoleLoggingIntegration` as a log
 * that correlates to the issue). `ApiError` (the expected 4xx flow) is handled
 * at the hook layer, not here. See docs/features/observability/design.md.
 */
/**
 * The router's `defaultErrorComponent`. Render-time crashes must keep
 * reaching <AppErrorBoundary> — Sentry capture, AppCrashPanel, and the reset
 * semantics live here. Rethrowing disables the router's own per-route error
 * UI so errors bubble out of the route tree instead of being swallowed per
 * route.
 */
export function RethrowToAppErrorBoundary({ error }: { error: unknown }): never {
  throw error;
}

export function AppErrorBoundary({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, eventId, resetError }) => (
        <AppCrashPanel error={error} eventId={eventId} onReload={resetError} />
      )}
      onError={(error, _componentStack, eventId) => {
        // Dev-visible label; consoleLoggingIntegration also forwards this as a
        // Sentry log carrying the eventId (correlates the log to the issue).
        console.error('[AppErrorBoundary]', { eventId }, error);
      }}
      onReset={() => {
        // Wipe the QueryClient so any cached error states are cleared before the
        // user retries, then bounce to the root so the router resolves fresh.
        queryClient.clear();
        window.location.assign('/');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
